import clsx from 'clsx';
import { format } from 'date-fns';
import { BiX } from 'react-icons/bi';
import { FiAlertCircle, FiArrowRight, FiClock } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';

import { useIsMobile } from '@/hooks/useIsMobile';
import { VESSEL_PARAM } from '@/pages/SettingsPage/components/settings-anchors';
import { UPGRADE_HREF } from '@/permissions/useAccessGate';

import ButtonPill from '../Button/ButtonPill';
import ButtonPillPrimary from '../Button/ButtonPillPrimary';
import { TextLink } from '../TextLink';

import s from './TrialBanner.module.scss';
import { type TrialReminder, useTrialReminder } from './useTrialReminder';

type Copy = { title: string; text: string; action: string };

const copyOf = ({ stage, daysLeft, endsAt, planLabel }: TrialReminder): Copy => {
  switch (stage) {
    case 'month':
      return {
        // "1 month" only while it is still true; past that the days are counted.
        title:
          daysLeft >= 28 ? '1 month of full access left' : `${daysLeft} days of full access left`,
        text: `Your ${planLabel} trial ends ${format(endsAt, 'd MMMM')}.`,
        action: 'See plans & prices',
      };
    case 'week':
      return {
        title: daysLeft === 1 ? 'Your trial ends tomorrow' : `Your trial ends in ${daysLeft} days`,
        text: 'Pick a plan to keep budgets, crew and logbooks running.',
        action: 'Choose a plan',
      };
    case 'lastDay':
      return {
        title: 'Last day of full access',
        text: 'Your offer ends tonight.',
        action: 'See plans & prices',
      };
  }
};

/** The plans screen opened straight on this vessel's plan, not on the list of vessels. */
const planHrefOf = (serviceId: string) => {
  const [path, query] = UPGRADE_HREF.split('?');
  const params = new URLSearchParams(query);
  params.set(VESSEL_PARAM, serviceId);
  return `${path}?${params.toString()}`;
};

const isOnPlansScreen = (pathname: string, search: string) => {
  const target = new URL(UPGRADE_HREF, window.location.origin);
  return (
    pathname === target.pathname &&
    new URLSearchParams(search).get('tab') === target.searchParams.get('tab')
  );
};

/**
 * The reminder that a trial is running out, shown a month, a week and a day before it does.
 * It brings its own data — see `useTrialReminder` — and renders nothing for anyone not on a
 * trial, so a page only decides where it sits: under the header on desktop, at the top of
 * home on mobile.
 *
 * Calm and closable a month out, a filled action and closable for a day a week out, and on
 * the last day it cannot be closed. Kept trial-specific for now; a general banner is a
 * design-system decision still to be made, and this is the first candidate to fold into it.
 *
 * @alias trial reminder, trial ending banner, upgrade nudge
 */
export const TrialBanner = ({ className }: { className?: string }) => {
  const { reminder, dismiss } = useTrialReminder();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // On the plans screen the banner would point at the page it is on.
  if (!reminder || isOnPlansScreen(pathname, search)) return null;

  const { stage } = reminder;
  const copy = copyOf(reminder);
  const href = planHrefOf(reminder.serviceId);
  const label = (
    <>
      {copy.action}
      <FiArrowRight aria-hidden="true" />
    </>
  );
  const Icon = stage === 'lastDay' ? FiAlertCircle : FiClock;

  // The filled actions are desktop's; on mobile the month and week ones fold to a link
  // under the title, and the last day's widens to the card.
  const action =
    stage === 'lastDay' ? (
      <ButtonPill className={s.button} onClick={() => navigate(href)}>
        {label}
      </ButtonPill>
    ) : stage === 'week' && !isMobile ? (
      <ButtonPillPrimary className={s.button} onClick={() => navigate(href)}>
        {label}
      </ButtonPillPrimary>
    ) : (
      <TextLink to={href} className={s.link}>
        {label}
      </TextLink>
    );

  return (
    <div className={clsx(s.banner, s[stage], className)} role="status">
      <Icon className={s.icon} aria-hidden="true" />
      <div className={s.copy}>
        <span className={s.title}>
          {copy.title}
          {isMobile ? '' : '.'}
        </span>{' '}
        <span className={s.text}>{copy.text}</span>
        {isMobile && stage !== 'lastDay' && action}
      </div>
      {(!isMobile || stage === 'lastDay') && action}
      {stage !== 'lastDay' && (
        <button type="button" className={s.close} onClick={dismiss} aria-label="Dismiss">
          <BiX aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
